import { useEffect, useMemo, useState } from "react";
import { resolveClinicalCosigner } from "../../../../../services/librariesCatalogService";
import { resolveUserRoleName } from "../../../../../constants";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";

/**
 * Loads Libraries Clinical Co-signers role mapping for the form creator role
 * and builds permissions (Save / To Be Sign vs Save / Sent to Office).
 */
export function useClinicalCosignPermissions({
  status,
  role,
  actorUserId,
  formFilledByUserId,
  formFilledByRole,
  serverPermissions,
}) {
  const [mapping, setMapping] = useState(null);
  const [loading, setLoading] = useState(false);

  const creatorRole = formFilledByRole || role || null;
  const creatorUserId = formFilledByUserId || actorUserId;

  useEffect(() => {
    if (!creatorRole && !creatorUserId) {
      setMapping(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = creatorRole
      ? { roleName: creatorRole }
      : { userId: creatorUserId };
    resolveClinicalCosigner(params)
      .then((res) => {
        if (cancelled) return;
        const asCreator = res?.asCreator || null;
        setMapping(
          asCreator
            ? {
                formCreatorRoleName: asCreator.formCreatorRoleName,
                formSignerRoleName: asCreator.formSignerRoleName,
              }
            : null,
        );
      })
      .catch(() => {
        if (!cancelled) setMapping(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [creatorRole, creatorUserId]);

  const permissions = useMemo(() => {
    const actorRole = resolveUserRoleName({ role }) || role;
    const local = buildClinicalFormPermissions(status || "pending", actorRole, {
      actorUserId,
      formFilledByRole: formFilledByRole || actorRole || null,
      mapping,
    });

    if (!serverPermissions) return local;

    return {
      ...serverPermissions,
      canEdit: local.canEdit,
      canSendToOffice: local.canSendToOffice,
      canToBeSign: local.canToBeSign,
      hideClinicalSignature: local.hideClinicalSignature,
      canSignSignatures: local.canSignSignatures,
      lockedReason: local.lockedReason ?? serverPermissions.lockedReason,
    };
  }, [
    status,
    role,
    actorUserId,
    formFilledByRole,
    mapping,
    serverPermissions,
  ]);

  return { permissions, cosignMapping: mapping, cosignLoading: loading };
}
